import styled, { createGlobalStyle } from "styled-components";
import { useState } from "react";
import { Link, router, usePage } from "@inertiajs/react";

import {
  guestPortal,
  insightsHome,
  privacyPath,
  propertyHome,
  termsPath,
} from "../../../components/insights/paths";
import COPY from "./copy";

// The HostelNMC brand page: one screen, a title, and three ways in. It is a
// signpost, not a brochure — the property page sells the rooms and the Insights
// site holds the writing, so anything more here only delays the click.
//
// It ships NO photography on purpose: the bundled images it could have used
// were AI renders and an unlicensed watermarked photo, all now removed from the
// repo. Real photos reach these pages through the CMS. See wiki/seo/README.md.
const INK = "#141414";
const GOLD = "#c9a86c";
const HAIRLINE = "rgba(255, 255, 255, 0.16)";

const PageStyle = createGlobalStyle`
  /* The UA focus ring is invisible on charcoal, and this page is almost entirely
     links. */
  :where(a, button):focus-visible {
    outline: 2px solid ${GOLD};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      transition-duration: 0.01ms !important;
    }
  }
`;

// min-height, not height + overflow:hidden. The page is sized to fit a screen
// without scrolling, but clipping would swallow content on a short window or at
// a large browser font size — worse than a few pixels of scroll.
const Page = styled.div`
  align-items: center;
  background: ${INK};
  color: #fff;
  display: flex;
  justify-content: center;
  min-height: 100dvh;
  padding: 32px 24px;
  position: relative;

  &::after {
    background: radial-gradient(
      circle at 50% 0%,
      rgba(201, 168, 108, 0.16),
      transparent 62%
    );
    content: "";
    inset: 0;
    pointer-events: none;
    position: absolute;
  }
`;

const Shell = styled.div`
  margin: 0 auto;
  max-width: 620px;
  position: relative;
  width: 100%;
  z-index: 1;
`;

const SkipLink = styled.a`
  background: #fff;
  color: ${INK};
  left: 12px;
  padding: 10px 16px;
  position: absolute;
  top: -60px;
  z-index: 100;

  &:focus {
    top: 12px;
  }
`;

const Eyebrow = styled.p`
  color: ${GOLD};
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.24em;
  margin-bottom: 18px;
  text-transform: uppercase;
`;

const Wordmark = styled.h1`
  font-size: clamp(2.4rem, 8vw, 3.6rem);
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1;
  text-transform: uppercase;

  b {
    color: ${GOLD};
    font-weight: 600;
  }
`;

const LogoImage = styled.img`
  display: block;
  height: clamp(48px, 11vw, 68px);
  width: auto;
`;

const Tagline = styled.p`
  color: #c8c2b6;
  font-size: clamp(0.98rem, 2.4vw, 1.1rem);
  line-height: 1.6;
  margin-top: 16px;
  max-width: 38ch;
`;

const Actions = styled.nav`
  display: grid;
  gap: 10px;
  margin-top: 34px;
`;

const Action = styled.a`
  align-items: center;
  background: ${(props) =>
    props.$primary ? "rgba(201,168,108,0.09)" : "transparent"};
  border: 1px solid ${(props) => (props.$primary ? GOLD : HAIRLINE)};
  color: #fff;
  display: grid;
  gap: 4px 20px;
  grid-template-columns: 1fr auto;
  padding: 18px 20px;
  transition:
    border-color 0.18s ease,
    background 0.18s ease;

  &:hover {
    background: rgba(201, 168, 108, 0.13);
    border-color: ${GOLD};
  }

  &:hover span[data-arrow] {
    transform: translateX(4px);
  }
`;

// Every cell is placed explicitly. Left to auto-placement the arrow claimed
// column 1 and shoved the title to the right edge.
const ActionTitle = styled.span`
  font-size: 1.02rem;
  font-weight: 600;
  grid-area: 1 / 1;
  letter-spacing: 0.01em;
`;

const ActionBody = styled.span`
  color: #a9a296;
  font-size: 0.88rem;
  grid-area: 2 / 1;
  line-height: 1.5;
`;

const Arrow = styled.span`
  align-self: center;
  color: ${GOLD};
  font-size: 1.15rem;
  grid-area: 1 / 2 / span 2 / 3;
  justify-self: end;
  transition: transform 0.18s ease;
`;

const Foot = styled.div`
  align-items: center;
  border-top: 1px solid ${HAIRLINE};
  color: #8e877c;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.8rem;
  gap: 10px 18px;
  justify-content: space-between;
  margin-top: 30px;
  padding-top: 18px;

  a {
    color: #8e877c;
  }

  a:hover {
    color: ${GOLD};
  }
`;

const FootLinks = styled.div`
  display: flex;
  gap: 18px;
`;

const LangGroup = styled.div`
  border: 1px solid ${HAIRLINE};
  display: flex;
  overflow: hidden;
`;

const LangButton = styled.button`
  background: ${(props) => (props.$active ? "#fff" : "transparent")};
  border: none;
  color: ${(props) => (props.$active ? INK : "#c8c2b6")};
  cursor: ${(props) => (props.$active ? "default" : "pointer")};
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  padding: 6px 10px;

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.$active ? "#fff" : "rgba(255,255,255,0.12)"};
  }
`;

// A typographic lockup, not an invented symbol: there is no HostelNMC logo asset
// in the repo. The CMS logo wins once one is uploaded — and falls back to the
// wordmark if that image fails, so one bad CMS URL cannot leave a broken image
// where the brand should be.
const Brandmark = (props) => {
  const { logoUrl, name } = props;
  const [logoFailed, setLogoFailed] = useState(false);

  if (logoUrl && !logoFailed) {
    return (
      <LogoImage src={logoUrl} alt={name} onError={() => setLogoFailed(true)} />
    );
  }

  const label = (name || "HostelNMC").toUpperCase();
  const split = label.lastIndexOf("NMC");

  if (split <= 0) {
    return <Wordmark>{label}</Wordmark>;
  }

  return (
    <Wordmark>
      {label.slice(0, split)}
      <b>{label.slice(split)}</b>
    </Wordmark>
  );
};

const Identity = (props) => {
  const page = usePage().props;
  const identity = page.identity || {};
  const language = page.locale === "id" ? "id" : "en";
  const copy = COPY[language];

  const brandName = identity.brand_name || "HostelNMC";
  const tagline = identity.tagline || copy.tagline;

  return (
    <Page>
      <PageStyle />
      <SkipLink href="#ways-in">{copy.skip}</SkipLink>

      <Shell>
        <Eyebrow>{copy.eyebrow}</Eyebrow>
        <Brandmark logoUrl={identity.logo_url} name={brandName} />
        <Tagline>{tagline}</Tagline>

        <Actions id="ways-in" aria-label={copy.navLabel}>
          <Action as={Link} href={propertyHome(language)} $primary>
            <ActionTitle>{copy.actions.property.title}</ActionTitle>
            <Arrow data-arrow aria-hidden="true">
              →
            </Arrow>
            <ActionBody>{copy.actions.property.body}</ActionBody>
          </Action>

          {/* A plain anchor, not an Inertia Link: /hebe/inhouse is a Rails
              redirect into the HGS guest app, which runs on a different layout
              and entrypoint — an SPA visit would try to resolve it as a page in
              this bundle. nofollow because it is an app entrance, not content
              worth a crawler's budget. */}
          <Action href={guestPortal()} rel="nofollow">
            <ActionTitle>{copy.actions.guest.title}</ActionTitle>
            <Arrow data-arrow aria-hidden="true">
              →
            </Arrow>
            <ActionBody>{copy.actions.guest.body}</ActionBody>
          </Action>

          <Action as={Link} href={insightsHome(language)}>
            <ActionTitle>{copy.actions.insights.title}</ActionTitle>
            <Arrow data-arrow aria-hidden="true">
              →
            </Arrow>
            <ActionBody>{copy.actions.insights.body}</ActionBody>
          </Action>
        </Actions>

        <Foot>
          <LangGroup>
            <LangButton
              type="button"
              $active={language === "en"}
              disabled={language === "en"}
              onClick={() => router.visit("/")}
              lang="en"
            >
              EN
            </LangButton>
            <LangButton
              type="button"
              $active={language === "id"}
              disabled={language === "id"}
              onClick={() => router.visit("/id")}
              lang="id"
            >
              ID
            </LangButton>
          </LangGroup>
          <FootLinks>
            <a href={privacyPath(language)}>{copy.privacy}</a>
            <a href={termsPath(language)}>{copy.terms}</a>
          </FootLinks>
        </Foot>
      </Shell>
    </Page>
  );
};

export default Identity;
